import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '@/app/session';
import { Icon } from '@/features/common/Icon';
import { StatusLine } from '@/features/common/StatusLine';
import { relativeTime } from '@/features/common/relativeTime';
import { InviteDialog } from '@/features/publicsite/community/InviteDialog';
import { SpaceMembers } from '@/features/publicsite/community/SpaceMembers';
import { spaceActions } from '@/features/publicsite/community/spaceRoster';
import { useForum } from '@/features/publicsite/community/useForum';
import { useInviteDialog } from '@/features/publicsite/community/useInviteDialog';
import { useSpaceMembership } from '@/features/publicsite/community/useSpaceMembership';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import { publicCommunity } from '@/i18n/dictionaries/publicCommunity';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/publicsite/community/space.css';

/**
 * A forum's own page: its topics, a way to start a new one, and — for a private space — its
 * roster. Reached both from the public /comunitate listing (public forums only) and from
 * /app/comunitate (a member's private spaces), which is why this lives under the public shell
 * rather than the cabinet: the same id resolves the same page either way, and the backend's own
 * membership gate is what actually decides who gets past `forbidden` below.
 */
export function ForumPage() {
  // The route param is the forum's slug for every link the site now hands out; the API accepts a
  // bare id too (GET /api/v1/forums/{idOrSlug}), so a link shared before slugs existed still
  // resolves without any parsing on this end.
  const { id = '' } = useParams();
  const { user } = useSession();
  const { t, language } = useTranslation(publicCommunity);
  const forumState = useForum(id);
  const { forum, topics, members, state, status, statusKind } = forumState;
  const [newTopic, setNewTopic] = useState('');
  const membersHeadingRef = useRef<HTMLHeadingElement>(null);
  const invite = useInviteDialog(id);
  const membership = useSpaceMembership(id, forum?.title ?? '', forumState);
  const userId = user?.userId ?? null;
  const actions = spaceActions(members ?? [], userId);

  usePublicPageTitle(
    state === 'missing'
      ? t('notFound')
      : state === 'forbidden'
        ? t('privateTitle')
        : (forum?.title ?? null),
  );

  const createTopic = async () => {
    await forumState.createTopic(newTopic);
    setNewTopic('');
  };

  if (state === 'loading') {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }
  if (state === 'forbidden') {
    return (
      <section className="container section">
        <h1>{t('privateTitle')}</h1>
        <p>{t('privateBody')}</p>
        <p>
          <Link to="/comunitate">{t('backToCommunity')}</Link>
        </p>
      </section>
    );
  }
  if (state === 'missing' || !forum) {
    return (
      <section className="container section">
        <h1>{t('notFound')}</h1>
        <p>
          <Link to="/comunitate">{t('backToCommunity')}</Link>
        </p>
      </section>
    );
  }

  return (
    <section className="container section">
      <Link className="detail-back" to="/comunitate">
        <Icon name="chevron-left" className="detail-back-icon" />
        {t('backToCommunity')}
      </Link>
      <p className="eyebrow">
        {forum.visibility === 'PRIVATE' ? t('badgePrivate') : t('badgePublic')}
      </p>
      <h1>{forum.title}</h1>
      {forum.description && <p className="muted">{forum.description}</p>}

      <h2>{t('topicsHeading')}</h2>
      {topics && topics.length === 0 && <p className="empty-state">{t('noTopics')}</p>}
      {topics && topics.length > 0 && (
        <div className="doc-list">
          {topics.map((tp) => (
            <Link
              key={tp.id}
              className="doc-row"
              to={`/comunitate/forumuri/${id}/subiecte/${tp.id}`}
            >
              <span>
                <span className="doc-row__title">{tp.title}</span>
                <span className="doc-row__meta">
                  {t('topicReplyCount', { count: tp.replyCount })} ·{' '}
                  {t('topicLastActivity', { when: relativeTime(tp.lastActivityAt, language) })}
                </span>
              </span>
              <Icon name="chevron-right" />
            </Link>
          ))}
        </div>
      )}

      {user ? (
        <p className="reply-form reply-form--center">
          <input
            className="reply-form__input"
            placeholder={t('newTopicPlaceholder')}
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
          />
          <button className="btn btn--primary" onClick={() => void createTopic()}>
            {t('newTopicAction')}
          </button>
        </p>
      ) : (
        <p>
          <a className="btn btn--primary btn--md" href="/saml/authenticate/mpass">
            <img className="btn__logo" src="/img/gov/mpass.svg" alt="" aria-hidden="true" />
            <span>{t('signInToParticipate')}</span>
          </a>
        </p>
      )}

      {forum.visibility === 'PRIVATE' && (
        <>
          <div className="space-members__head">
            <h2 ref={membersHeadingRef} tabIndex={-1}>
              {t('membersHeading')}
            </h2>
            {actions.invite && (
              <button type="button" className="btn btn--primary" onClick={() => void invite.show()}>
                {t('invite')}
              </button>
            )}
          </div>
          <StatusLine status={status} kind={statusKind} />
          {members && members.length > 0 && (
            <SpaceMembers
              members={members}
              actions={actions}
              userId={userId}
              onRemove={(member) => void membership.remove(member)}
            />
          )}
          {actions.leave && (
            <p>
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => void membership.leave()}
              >
                {t('leaveSpace')}
              </button>
            </p>
          )}
          <InviteDialog invite={invite} fallbackFocus={membersHeadingRef} />
          {invite.confirmDialog}
          {membership.confirmDialog}
        </>
      )}
    </section>
  );
}
